import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/layout/Layout';
import NotificationsPanel from '../../components/dashboard/NotificationsPanel';
import WelcomePanel from '../../components/dashboard/WelcomePanel';
import ActivationChecklist from '../../components/dashboard/ActivationChecklist';
import AvisoMenuNuevo from '../../components/dashboard/AvisoMenuNuevo';
import WeeklyPulsePanel from '../../components/dashboard/WeeklyPulsePanel';
import TodayActionsPanel from '../../components/dashboard/TodayActionsPanel';
import QuickActionsGrid from '../../components/dashboard/QuickActionsGrid';
import VisitsTrendsPanel from '../../components/dashboard/VisitsTrendsPanel';
import AdminHomePanel from '../../components/dashboard/AdminHomePanel';
import DetalleCrecimiento from '../../components/dashboard/DetalleCrecimiento';
import { billingApi } from '../../api/billingApi';
import { fetchSubscriptionAlerts } from '../../services/AdminSubscriptionService';
import type { SubscriptionAlerts } from '../../services/AdminSubscriptionService';

const Dashboard: React.FC = () => {
  const { currentUser, markInstructionsDismissed } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const [pendingPayments, setPendingPayments] = useState(0);
  const [subscriptionAlerts, setSubscriptionAlerts] = useState<SubscriptionAlerts>({
    expired: { count: 0, users: [] },
    expiringSoon: { count: 0, users: [] }
  });
  const [, setLoadingAlerts] = useState(false);

  // <= 10 (no < 10): al cruzar la meta, el checklist muestra la celebración
  // una vez y el dueño la despide con "Entendido" (instructionsDismissed).
  const showOnboarding =
    !isAdmin &&
    currentUser?.hasCompletedOnboarding === true &&
    !currentUser?.instructionsDismissed &&
    (currentUser?.registeredClients || 0) <= 10;

  const handleDismissOnboarding = async () => {
    try {
      await markInstructionsDismissed();
    } catch (error) {
      console.error('[Dashboard] Error dismissing onboarding instructions:', error);
    }
  };

  useEffect(() => {
    const fetchPendingPayments = async () => {
      if (!isAdmin) return;

      try {
        // Pagos pendientes desde lealify-base (reemplaza el query Firestore directo).
        const payments = await billingApi.adminListPayments();
        // Solo manuales: son los únicos que el admin puede aprobar. Un checkout de
        // pasarela 'pending' es un intento abandonado (o aún en curso), no una tarea.
        setPendingPayments(payments.filter(p => p.status === 'pending' && p.method === 'manual').length);
      } catch (error) {
        console.error('Error fetching pending payments:', error);
      }
    };

    fetchPendingPayments();
  }, [isAdmin]);

  useEffect(() => {
    const loadSubscriptionAlerts = async () => {
      if (!isAdmin) return;

      setLoadingAlerts(true);
      try {
        const alerts = await fetchSubscriptionAlerts();
        setSubscriptionAlerts(alerts);
      } catch (error) {
        console.error('Error loading subscription alerts:', error);
      } finally {
        setLoadingAlerts(false);
      }
    };

    loadSubscriptionAlerts();
  }, [isAdmin]);

  return (
    <Layout>
      <div className="space-y-6">
        {showOnboarding && (
          <div className="animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
            <ActivationChecklist onDismiss={handleDismissOnboarding} />
          </div>
        )}

        {/* Franja azul de la marca (rediseño opción B, 8-oct-2026): saludo + cifras de la semana,
            como la banda de números de lealify.com. .banda-marca (index.css) pone blanco sobre azul. */}
        {!isAdmin && (
          <div className="banda-marca animate-fade-in-up rounded-2xl bg-[#0057B8] p-2 md:p-3 shadow-lg shadow-[#0057B8]/20" style={{ animationDelay: '0.1s' }}>
            <WelcomePanel />
            {currentUser?.hasCompletedOnboarding === true && (
              <div className="px-4 pb-4 md:px-4 md:pb-3">
                <WeeklyPulsePanel />
              </div>
            )}
          </div>
        )}

        {!isAdmin && (
          <div className="animate-fade-in-up space-y-3" style={{ animationDelay: '0.15s' }}>
            {/* F4 reorden del menú (24-oct-2026): aviso único «dónde quedó cada cosa», solo a quien ya conocía el panel. */}
            <AvisoMenuNuevo />
            <NotificationsPanel />
          </div>
        )}

        {/* Acciones del día + accesos: solo con el negocio ya configurado */}
        {!isAdmin && currentUser?.hasCompletedOnboarding === true && (
          <>
            {/* Pantalla ancha: gráfica de visitas (3/5) junto a «Para hoy» (2/5), como la maqueta B. */}
            <div className="grid gap-6 xl:grid-cols-5 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
              <div className="xl:col-span-3 min-w-0"><VisitsTrendsPanel /></div>
              <div className="xl:col-span-2 min-w-0"><TodayActionsPanel /></div>
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
              <QuickActionsGrid />
            </div>
          </>
        )}

        {isAdmin && (
          <div className="animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
            <AdminHomePanel
              expiredCount={subscriptionAlerts.expired.count}
              expiringSoonCount={subscriptionAlerts.expiringSoon.count}
              pendingPayments={pendingPayments}
            />
          </div>
        )}
        {isAdmin && (
          <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <DetalleCrecimiento />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;